import { computed, inject, provide, reactive, readonly, shallowRef, type DeepReadonly, type InjectionKey } from 'vue';
import type { FormField } from '@/models/form';
import { isCalculatedField, isLockedCalculatedField, isOverridden, withOverrideMark } from '../effects';

/**
 * The field item context (ADR 0011, ENG-9040 decisions 0, 3, 4, 6 and 8). EntryBaseItem provides
 * one per field through `provideFieldItem()`, and an item reads it with one `useFieldItem()` call.
 * A matrix cell adds a cell context on top, through `provideMatrixCell()` and `useMatrixCell()`.
 * This file is the only code that calls `inject` for either.
 *
 * An item reads the field from here and never writes into it: every write is a `change()`, which
 * applies the patch to the store's field and marks the field touched and changed, and which a
 * read-only field refuses. An item never marks itself touched, and takes neither the field nor
 * whether it is read-only as a prop.
 */

/**
 * Why a field can't be changed, or null when it can. `view` is the view screen, `calculated` a
 * Calculated field the form doesn't let the reader override, and `disabled` a Disable effect.
 * All three draw the same read-only way; only the hint beside the question tells them apart.
 */
export type FieldReadOnly = 'view' | 'calculated' | 'disabled' | null;

/**
 * What the form's logic did to the field, which EntryBaseItem draws as a chip beside the question (ENG-9765), on the
 * edit screen and the view screen. `calculated` is a Calculated field the reader can't change, or any Calculated field
 * on the view screen; `calculatedOverridable` one whose form allows an override, in edit mode; `overridden` one the
 * reader overrode ("Your answer", or "Overridden" on the view screen); and `disabled` a Disable effect ("Set by the
 * form").
 */
export type FieldHint = 'calculated' | 'calculatedOverridable' | 'overridden' | 'disabled' | null;

/** What an item hands `change()`: the answer keys it sets, as they are stored on the field. */
export type FieldPatch = Partial<FormField>;

/** How a `change()` counts, beyond the patch it applies. Left out, a write marks the field touched and nothing more. */
export interface FieldChangeOptions {
  /**
   * False for a write that empties the answer rather than giving one, such as a date's Reset: the field is still
   * marked changed, so the effects run again, and touched stays as it was.
   */
  touch?: boolean;
  /**
   * Also empties every field whose conditions name this one, and the fields that name those, so a field calculated
   * from an answer the reader cleared doesn't keep what was worked out from it (`resetCalculatedValue` in effects.ts).
   */
  clearDependents?: boolean;
}

/**
 * Something an item's control ran into that is not part of the answer, such as a location the browser refused or a
 * number range outside its bounds. The item hands it to `showMessage()`, and EntryBaseItem draws it in the field's
 * messages after the field's own.
 */
export interface FieldItemMessage {
  readonly text: string;
  /** `warn` draws it the way a Warn effect's message draws. Left out, it draws as an error. */
  readonly tone?: 'warn';
  /** A button drawn after the text, such as Try again. */
  readonly action?: { readonly label: string; readonly run: () => void };
}

export interface FieldItem {
  /** The field to draw, read-only: a write to it is refused, so write through `change()`. */
  readonly field: DeepReadonly<FormField>;
  /** The question's `h3`, which the primary input is `aria-labelledby`. */
  readonly questionId: string;
  /** The field's messages: its live region, always in the DOM. */
  readonly errorId: string;
  /** The chip beside the question, drawn only while `hint` is set. */
  readonly hintId: string;
  /**
   * The ids the primary input is `aria-describedby`: the messages, then the hint when there is one. An item may add one
   * of its own after them, as the number's limits under its box (EntryItemTextAndNumber.vue).
   */
  readonly describedBy: string;
  readonly readOnly: FieldReadOnly;
  readonly hint: FieldHint;
  /** The reader changed the field through its control, or Save counted it. The field row no longer waits on it. */
  readonly touched: boolean;
  /**
   * The answer is wrong: an Error effect fired, or Save found a matrix's rows break a rule. A missing answer is not a
   * wrong one, so a required field left unanswered is never invalid; the row draws it amber instead (ENG-9764).
   */
  readonly invalid: boolean;
  /** Strict-required: Save stops on it unanswered. The row says "Required", and the primary input `aria-required`. */
  readonly required: boolean;
  /** Recommended or soft-required, neither of which stops Save. The row says "Recommended". */
  readonly recommended: boolean;
  /** The message the item last handed `showMessage()`, or null. */
  readonly message: FieldItemMessage | null;
  /** Reveal just took the reader to this field, and EntryBaseItem is blinking it. Entry holds which field that is. */
  readonly revealed: boolean;
  /**
   * Applies `patch` to the field and marks it touched and changed, unless `options` says otherwise. The only write an
   * item makes, and refused while `readOnly` is set, so a read-only control's events write nothing. On a Calculated
   * field the reader may override, it also sets or takes off the override mark (`withOverrideMark` in effects.ts).
   */
  change(patch: FieldPatch, options?: FieldChangeOptions): void;
  /**
   * Draws `message` in the field's messages, in place of the one the item showed before, or takes it away with null.
   * It writes nothing into the field, so a read-only field takes one too.
   */
  showMessage(message: FieldItemMessage | null): void;
}

/** Where a context reads from. EntryBaseItem passes the store; `mountFieldItem` passes a fake. */
export interface FieldItemSource {
  field: () => FormField;
  /** The entry is on the view screen (`screen === 'view'`, ADR 0010). */
  view: () => boolean;
  touched: () => boolean;
  /** This is the field Entry's `revealedFieldId` names. */
  revealed: () => boolean;
  /** Takes any write: the context's `change()` is what refuses a read-only field's. */
  change: (patch: FieldPatch, options?: FieldChangeOptions) => void;
}

const FIELD_ITEM: InjectionKey<FieldItem> = Symbol('fieldItem');

/**
 * The id of one part of a field's markup, unique on the page because the field's identifier is: the
 * context's question, error and hint ids, and the ids an item gives its own controls, such as a
 * choice's options.
 */
export const fieldPartId = (identifier: string, part: string) => `entry-field-${identifier}-${part}`;

/**
 * What an item's primary input says about itself to a screen reader (ENG-9040 decision 8). The question names it, and
 * its messages and hint describe it, then `ownDescriptionId` when the item draws a line of its own under the input,
 * as the number's limits. A flag that is false is left off rather than drawn as "false", as PrimeVue draws its own
 * `aria-invalid`.
 */
export const primaryInputAria = (item: FieldItem, ownDescriptionId?: string | null) => ({
  'aria-labelledby': item.questionId,
  'aria-describedby': ownDescriptionId ? `${item.describedBy} ${ownDescriptionId}` : item.describedBy,
  'aria-invalid': item.invalid || undefined,
  'aria-required': item.required || undefined,
  'aria-readonly': item.readOnly !== null || undefined
});

/** The id of one part of an item's own markup, as `fieldPartId` gives it, following the field the item draws. */
export const itemPartId = (item: FieldItem, part: string) => computed(() => fieldPartId(item.field.identifier, part));

/** Why a field's item takes no change, or `null` when it takes them. Save reads it too, for an item that isn't drawn. */
export const readOnlyOf = (field: FormField, view: boolean): FieldReadOnly => {
  if (view) {
    return 'view';
  }
  if (isLockedCalculatedField(field)) {
    return 'calculated';
  }
  return field.disable ? 'disabled' : null;
};

/** The chip beside the question. Read from the field rather than from `readOnly`, which says only "view" there. */
export const hintOf = (field: FormField, view: boolean): FieldHint => {
  if (isOverridden(field)) {
    return 'overridden';
  }
  if (isCalculatedField(field)) {
    return view || isLockedCalculatedField(field) ? 'calculated' : 'calculatedOverridable';
  }
  return field.disable ? 'disabled' : null;
};

const createFieldItem = (source: FieldItemSource): FieldItem => {
  const field = computed(source.field);
  const idOf = (part: string) => computed(() => fieldPartId(field.value.identifier, part));
  const errorId = idOf('error');
  const hintId = idOf('hint');
  const readOnly = computed(() => readOnlyOf(field.value, source.view()));
  const hint = computed(() => hintOf(field.value, source.view()));
  const touched = computed(source.touched);
  const message = shallowRef<FieldItemMessage | null>(null);

  return readonly(reactive({
    field,
    questionId: idOf('question'),
    errorId,
    hintId,
    describedBy: computed(() => hint.value ? `${errorId.value} ${hintId.value}` : errorId.value),
    readOnly,
    hint,
    touched,
    invalid: computed(() => !!field.value.error || !!field.value.invalidInput),
    required: computed(() => !!field.value.strictRequired),
    recommended: computed(() => !!(field.value.recommended || field.value.nonStrictRequired)),
    message,
    revealed: computed(source.revealed),
    change: (patch: FieldPatch, options?: FieldChangeOptions) => {
      if (readOnly.value === null) {
        source.change(withOverrideMark(field.value, patch, options?.touch !== false), options);
      }
    },
    showMessage: (next: FieldItemMessage | null) => {
      message.value = next;
    }
  })) as FieldItem;
};

/** Called by EntryBaseItem, once per field. Returns the context it provides, for its own template. */
export const provideFieldItem = (source: FieldItemSource): FieldItem => {
  const item = createFieldItem(source);
  provide(FIELD_ITEM, item);
  return item;
};

/** The one call an item makes for its context. It throws outside EntryBaseItem or `mountFieldItem`. */
export const useFieldItem = (): FieldItem => {
  const item = inject(FIELD_ITEM, undefined);
  if (!item) {
    throw new Error('useFieldItem() needs a field item above it: EntryBaseItem, or mountFieldItem in a test.');
  }
  return item;
};

/**
 * What a matrix cell adds to the matrix field's context (ENG-9040 decisions 6 and 8): the headers that name the cell's
 * control, and ids for the cell's own markup. MatrixCell provides one per cell, from the row and column the matrix
 * draws it in, because a provide in the matrix itself would be one for every cell. The cell reads the rest, the field
 * and whether it is read-only, from the matrix field's context.
 */
export interface MatrixCellItem {
  /** The row the cell is drawn in, as the matrix's rows name it. */
  readonly rowId: string;
  /** The column the cell is drawn in, as the form's columns name it. */
  readonly columnId: string | number;
  /** The row's header, in the matrix's frozen first column. */
  readonly rowHeaderId: string;
  /** The column's header, in the matrix's header row. */
  readonly columnHeaderId: string;
  /** The id of one part of the cell's own markup, unique on the page because the field, row and column ids are. */
  partId(part: string): string;
}

/** Where a cell context reads from. MatrixCell passes its own props. */
export interface MatrixCellSource {
  fieldId: () => string;
  rowId: () => string;
  columnId: () => string | number;
}

const MATRIX_CELL: InjectionKey<MatrixCellItem> = Symbol('matrixCell');

/** The id MatrixCell gives a row's header, which every cell in the row is labelled by. */
export const matrixRowHeaderId = (fieldId: string, rowId: string) => fieldPartId(fieldId, `row-${rowId}`);

/** The id the matrix gives a column's header, which every cell in the column is labelled by. */
export const matrixColumnHeaderId = (fieldId: string, columnId: string | number) =>
  fieldPartId(fieldId, `column-${columnId}`);

/** Why a matrix column's cells are locked: the form disables the column, or the column shows the case's shifted dates. */
export type MatrixColumnLock = 'locked' | 'date-locked';

/** The id of the reason a column's header gives for a lock, which each locked cell in the column is described by. */
export const matrixColumnLockId = (fieldId: string, columnId: string | number, lock: MatrixColumnLock) =>
  fieldPartId(fieldId, `column-${columnId}-${lock}`);

/** Called by MatrixCell, once per cell. Returns the context it provides, for its own template. */
export const provideMatrixCell = (source: MatrixCellSource): MatrixCellItem => {
  const cell = readonly(reactive({
    rowId: computed(source.rowId),
    columnId: computed(source.columnId),
    rowHeaderId: computed(() => matrixRowHeaderId(source.fieldId(), source.rowId())),
    columnHeaderId: computed(() => matrixColumnHeaderId(source.fieldId(), source.columnId())),
    partId: (part: string) => fieldPartId(source.fieldId(), `cell-${source.rowId()}-${source.columnId()}-${part}`)
  })) as MatrixCellItem;
  provide(MATRIX_CELL, cell);
  return cell;
};

/** The cell an item is drawn in, or null for an item drawn as a field. */
export const useMatrixCell = (): MatrixCellItem | null => inject(MATRIX_CELL, null);
